/**
 * Native cart recommendation presentation. Consumes window.CaffeineLearning.
 * Does not rank, patch fetch, or load the app recommendation renderer.
 */
(function (root) {
  if (root.NativeCartRecommendations) return;

  var MOUNT = '[data-native-cart-recommendations]';
  var MAX = 2;
  var INT_RE = /^[1-9][0-9]*$/;
  var rankToken = 0;
  var variantCache = {};
  var pendingRows = new WeakSet();
  var pendingVariants = new Set();
  var lastRenderedIdentity = '';
  var quickbuy = null;
  var quickbuySession = 0;
  var swallowEscape = false;
  var rowNotice = null;

  function learning() {
    var engine = root.CaffeineLearning;
    return engine && typeof engine.rank === 'function' ? engine : null;
  }

  function readConfig() {
    if (typeof document === 'undefined') {
      return { title: 'Pairs well with', limit: MAX, preview: false, catalogPublished: false };
    }
    var node = document.getElementById('NativeCartLearningConfig');
    if (!node || !node.textContent) {
      return { title: 'Pairs well with', limit: MAX, preview: false, catalogPublished: false };
    }
    try {
      var parsed = JSON.parse(node.textContent);
      var limit = Number(parsed.limit);
      return {
        title: typeof parsed.title === 'string' && parsed.title ? parsed.title : 'Pairs well with',
        limit: Number.isFinite(limit) && limit > 0 ? Math.min(MAX, limit) : MAX,
        preview: parsed.preview === true,
        catalogPublished: parsed.catalogPublished === true
      };
    } catch (err) {
      return { title: 'Pairs well with', limit: MAX, preview: false, catalogPublished: false };
    }
  }

  function mountLimit(el) {
    var raw = el && el.getAttribute('data-limit');
    var limit = Number(raw);
    if (Number.isFinite(limit) && limit > 0) return Math.min(8, limit);
    return readConfig().limit;
  }

  function strings() {
    return (root.theme && root.theme.strings && root.theme.strings.recommendations) || {};
  }

  function routes() {
    return (root.theme && root.theme.routes) || {};
  }

  function shopRoot() {
    return (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
  }

  function localizedUrl(key) {
    var value = routes()[key];
    if (!value) throw new Error('missing_route');
    var join = value.indexOf('?') === -1 ? '?' : '&';
    return value + join + 't=' + Date.now();
  }

  function formatMoney(cents) {
    var n = Number(cents);
    if (!Number.isFinite(n)) return '';
    if (root.theme && root.theme.Currency && typeof root.theme.Currency.formatMoney === 'function') {
      var format = (root.theme.settings && root.theme.settings.moneyFormat) || '${{amount}}';
      return root.theme.Currency.formatMoney(n, format);
    }
    return '$' + (n / 100).toFixed(2);
  }

  function mountPlacement(el) {
    return (el && el.getAttribute('data-placement')) || 'cart_drawer';
  }

  function mounts() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return [];
    return Array.prototype.slice.call(document.querySelectorAll(MOUNT)).filter(function (el) {
      return mountPlacement(el) === 'cart_drawer';
    });
  }

  function productMounts() {
    if (typeof document === 'undefined' || !document.querySelectorAll) return [];
    return Array.prototype.slice.call(document.querySelectorAll(MOUNT)).filter(function (el) {
      return mountPlacement(el) === 'product_page';
    });
  }

  function hideMounts(nodes) {
    destroyQuickbuy(false, 'cart_drawer');
    lastRenderedIdentity = '';
    (nodes || mounts()).forEach(function (el) {
      el.replaceChildren();
    });
  }

  function cartIdentity(cart) {
    if (!cart || !Array.isArray(cart.items)) return '';
    return JSON.stringify((cart.items || []).map(function (line) {
      return [String(line.key), line.quantity, line.final_line_price];
    }).sort(function (a, b) {
      return String(a[0]).localeCompare(String(b[0]));
    }));
  }

  function currentCart(hint, fresh) {
    if (hint && Array.isArray(hint.items)) return Promise.resolve(hint);
    if (!fresh && root.CaffeineCart && typeof root.CaffeineCart.getLastCart === 'function') {
      var last = root.CaffeineCart.getLastCart();
      if (root.CaffeineCart.isFullCart && root.CaffeineCart.isFullCart(last)) {
        return Promise.resolve(last);
      }
    }
    var url = localizedUrl('cart');
    return root.fetch(url, {
      credentials: 'same-origin',
      method: 'GET',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      return response.text().then(function (text) {
        return root.CaffeineCart.parseJsonResponse(response, text);
      });
    });
  }

  function isDirect(product) {
    return Number(product && product.variantCount) === 1;
  }

  function selectedVariantId(row) {
    return String((row && row.getAttribute('data-variant-id')) || '').trim();
  }

  function setStatus(row, message, isError) {
    if (!row || !row.isConnected) return;
    var status = row.querySelector('[data-native-cart-rec-status]');
    if (!status) return;
    status.hidden = !message;
    status.textContent = message || '';
    status.classList.toggle('is-error', !!isError);
  }

  function addEnabled(row) {
    return !!(row && row.getAttribute('data-variant-id'));
  }

  function setRowPending(row, pending) {
    if (!row || !row.isConnected) return;
    row.classList.toggle('is-loading', pending);
    if (pending) {
      pendingRows.add(row);
      row.setAttribute('aria-busy', 'true');
    } else {
      pendingRows.delete(row);
      row.removeAttribute('aria-busy');
    }
    var add = row.querySelector('[data-native-cart-rec-add]');
    if (add) {
      add.disabled = pending || !addEnabled(row);
      var label = add.querySelector('[data-native-cart-rec-add-label]');
      if (label) label.textContent = pending ? (strings().adding || 'Adding…') : (strings().add || 'Add');
      if (pending) add.setAttribute('aria-busy', 'true');
      else add.removeAttribute('aria-busy');
    }
    if (pending) setStatus(row, '', false);
  }

  function productJsUrl(handle) {
    return shopRoot() + 'products/' + encodeURIComponent(handle) + '.js';
  }

  function loadVariants(handle) {
    if (!handle) return Promise.reject(new Error('missing_handle'));
    if (variantCache[handle]) return variantCache[handle];
    var request = root.fetch(productJsUrl(handle), {
      credentials: 'same-origin',
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(2000) : undefined
    }).then(function (response) {
      if (!response.ok) throw new Error('product_unavailable');
      return response.json();
    }).then(function (live) {
      if (!live || String(live.handle) !== String(handle) || !Array.isArray(live.variants)) {
        throw new Error('product_mismatch');
      }
      return live;
    });
    variantCache[handle] = request;
    request.catch(function () {
      if (variantCache[handle] === request) delete variantCache[handle];
    });
    return request;
  }

  function variantImageSrc(variant) {
    if (!variant) return '';
    var image = variant.featured_image || variant.image;
    if (image && typeof image === 'object') {
      image = image.src || (image.preview_image && image.preview_image.src) || '';
    }
    if (!image && variant.featured_media) {
      var media = variant.featured_media;
      image = (media.preview_image && media.preview_image.src) || media.src || '';
    }
    if (typeof image !== 'string' || !image) return '';
    if (image.indexOf('//') === 0) return 'https:' + image;
    return image;
  }

  function productUrl(product) {
    if (product && product.url) return product.url;
    var handle = product && product.handle;
    return shopRoot() + 'products/' + encodeURIComponent(handle || '');
  }

  function quickbuyHost(placement) {
    if (typeof document === 'undefined') return null;
    if (placement === 'product_page') return document.body || document.documentElement;
    var drawer = document.getElementById('CartDrawer');
    if (drawer && !drawer.hidden && drawer.getAttribute('aria-hidden') !== 'true') return drawer;
    if (placement === 'cart_drawer' && drawer) return drawer;
    return document.body || document.documentElement;
  }

  function focusableIn(container) {
    if (!container || !container.querySelectorAll) return [];
    return Array.prototype.slice.call(container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]'
    )).filter(function (el) {
      if (el.disabled || el.hidden) return false;
      if (el.getAttribute('aria-hidden') === 'true') return false;
      var tabindex = el.getAttribute('tabindex');
      if (tabindex === '-1') return false;
      return true;
    });
  }

  function restoreQuickbuyFocus(trigger) {
    var next = trigger && trigger.isConnected ? trigger : null;
    if (!next && typeof document !== 'undefined') {
      var drawer = document.getElementById('CartDrawer');
      if (drawer) {
        next = drawer.querySelector('.js-drawer-close');
        if (!next || !next.isConnected) {
          next = drawer;
          if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
        }
      }
    }
    if (next && typeof next.focus === 'function') next.focus();
    return next;
  }

  function destroyQuickbuy(restoreTrigger, placement) {
    if (placement && quickbuy && quickbuy.placement !== placement) return;
    quickbuySession += 1;
    var state = quickbuy;
    quickbuy = null;
    if (!state || !state.el) return;
    var trigger = restoreTrigger === false ? null : state.trigger;
    if (document.activeElement && state.el.contains(document.activeElement) && document.activeElement.blur) {
      document.activeElement.blur();
    }
    if (typeof state.el.close === 'function') {
      try { state.el.close(); } catch (err) { /* already closed */ }
    }
    if (state.el.parentNode) state.el.parentNode.removeChild(state.el);
    restoreQuickbuyFocus(trigger);
  }

  function trapQuickbuyTab(evt) {
    if (!quickbuy || !quickbuy.el) return;
    var items = focusableIn(quickbuy.el);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    var active = document.activeElement;
    if (evt.shiftKey && (active === first || !quickbuy.el.contains(active))) {
      evt.preventDefault();
      last.focus();
    } else if (!evt.shiftKey && (active === last || !quickbuy.el.contains(active))) {
      evt.preventDefault();
      first.focus();
    }
  }

  function isEscape(evt) {
    return evt.key === 'Escape' || evt.key === 'Esc' || evt.keyCode === 27;
  }

  function onQuickbuyKeyDown(evt) {
    if (!quickbuy || !quickbuy.el) return;
    if (isEscape(evt)) {
      swallowEscape = true;
      evt.preventDefault();
      evt.stopPropagation();
      destroyQuickbuy(true);
      return;
    }
    if (evt.key === 'Tab' || evt.keyCode === 9) trapQuickbuyTab(evt);
  }

  function onQuickbuyKeyUp(evt) {
    if (!swallowEscape || !isEscape(evt)) return;
    swallowEscape = false;
    evt.preventDefault();
    evt.stopPropagation();
    evt.stopImmediatePropagation();
  }

  function setQuickbuyNote(dialog, message) {
    var note = dialog && dialog.querySelector('[data-native-cart-quickbuy-note]');
    if (!note) return;
    note.hidden = !message;
    note.textContent = message || '';
  }

  function setQuickbuyError(dialog, message) {
    var status = dialog && dialog.querySelector('[data-native-cart-quickbuy-status]');
    if (!status) return;
    status.hidden = !message;
    status.textContent = message || '';
  }

  function syncQuickbuyAdd(dialog) {
    var add = dialog && dialog.querySelector('[data-native-cart-rec-add]');
    if (!add) return;
    var busy = dialog.classList.contains('is-loading');
    add.disabled = busy || !(quickbuy && quickbuy.selected && quickbuy.selected.available === true);
    var label = add.querySelector('[data-native-cart-rec-add-label]');
    if (label) label.textContent = busy ? (strings().adding || 'Adding…') : (strings().add || 'Add');
    if (busy) add.setAttribute('aria-busy', 'true');
    else add.removeAttribute('aria-busy');
  }

  function moneyAmount(value) {
    var amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  function availablePrices(variants) {
    var prices = [];
    (variants || []).forEach(function (variant) {
      if (!variant || variant.available !== true) return;
      var amount = moneyAmount(variant.price);
      if (amount != null) prices.push(amount);
    });
    return prices;
  }

  function pricesDiffer(variants) {
    var prices = availablePrices(variants);
    if (prices.length < 2) return false;
    return prices.some(function (price) { return price !== prices[0]; });
  }

  function fromPriceText(product, variants) {
    var prices = availablePrices(variants);
    var amount = prices.length ? Math.min.apply(null, prices) : moneyAmount(product && (product.price != null ? product.price : product.priceCents));
    if (amount == null) return '';
    return (strings().fromPrice || 'From') + ' ' + formatMoney(amount);
  }

  function rememberRowNotice(row, message, confirmed) {
    if (!row || !row.getAttribute) return;
    rowNotice = {
      id: row.getAttribute('data-product-id'),
      handle: row.getAttribute('data-handle'),
      message: message,
      confirmed: !!confirmed
    };
  }

  function applyRowNotice(row) {
    if (!rowNotice || !row || !row.isConnected) return;
    if (row.getAttribute('data-product-id') !== rowNotice.id) return;
    if (row.getAttribute('data-handle') !== rowNotice.handle) return;
    setStatus(row, rowNotice.message, true);
    if (!rowNotice.confirmed) return;
    var add = row.querySelector('[data-native-cart-rec-add]');
    if (add) add.disabled = true;
    row.classList.add('is-confirmed');
  }

  function cloneProduct(product) {
    if (!product) return null;
    var copy = {};
    Object.keys(product).forEach(function (key) {
      copy[key] = product[key];
    });
    return copy;
  }

  function lockQuickbuyChoices(dialog, locked) {
    if (!dialog || !dialog.querySelectorAll) return;
    dialog.querySelectorAll('[data-native-cart-quickbuy-variant]').forEach(function (input) {
      if (locked) {
        if (!input.disabled) input.setAttribute('data-native-cart-quickbuy-locked', 'true');
        input.disabled = true;
        return;
      }
      if (input.getAttribute('data-native-cart-quickbuy-locked') === 'true') {
        input.disabled = false;
        input.removeAttribute('data-native-cart-quickbuy-locked');
      }
    });
  }

  function sameQuickbuy(dialog, session) {
    return !!(dialog && quickbuy && quickbuy.el === dialog && quickbuy.session === session && dialog.isConnected);
  }

  function applySelectedVariant(variant) {
    if (!quickbuy || !quickbuy.el || !variant || variant.available !== true) return;
    quickbuy.selected = variant;
    var priceEl = quickbuy.el.querySelector('[data-native-cart-quickbuy-price]');
    if (priceEl) priceEl.textContent = formatMoney(variant.price);
    var img = quickbuy.el.querySelector('[data-native-cart-quickbuy-image]');
    var src = variantImageSrc(variant) || (quickbuy.product && quickbuy.product.image) || '';
    if (img && src) {
      img.src = src;
      img.hidden = false;
    }
    setQuickbuyError(quickbuy.el, '');
    syncQuickbuyAdd(quickbuy.el);
  }

  function renderQuickbuyChoices(dialog, live, product) {
    if (!dialog) return;
    setQuickbuyNote(dialog, '');
    setQuickbuyError(dialog, '');
    var choices = dialog.querySelector('[data-native-cart-quickbuy-choices]');
    if (!choices) return;
    choices.replaceChildren();
    var byId = {};
    (live.variants || []).forEach(function (variant) {
      if (!variant || !INT_RE.test(String(variant.id))) return;
      byId[String(variant.id)] = variant;
      var label = dialog.ownerDocument.createElement('label');
      label.className = 'native-cart-quickbuy__choice';
      if (variant.available !== true) label.classList.add('is-sold-out');
      var input = dialog.ownerDocument.createElement('input');
      input.type = 'radio';
      input.name = 'native-cart-quickbuy-variant';
      input.value = String(variant.id);
      input.className = 'native-cart-quickbuy__radio';
      input.setAttribute('data-native-cart-quickbuy-variant', '');
      input.disabled = variant.available !== true;
      var text = dialog.ownerDocument.createElement('span');
      text.className = 'native-cart-quickbuy__choice-label';
      text.textContent = variant.title || String(variant.id);
      label.appendChild(input);
      label.appendChild(text);
      if (pricesDiffer(live.variants)) {
        var choicePrice = dialog.ownerDocument.createElement('span');
        choicePrice.className = 'native-cart-quickbuy__choice-price';
        choicePrice.textContent = formatMoney(variant.price);
        label.appendChild(choicePrice);
      }
      if (variant.available !== true) {
        var sold = dialog.ownerDocument.createElement('span');
        sold.className = 'native-cart-quickbuy__soldout';
        sold.textContent = strings().soldOut || 'Sold out';
        label.appendChild(sold);
      }
      choices.appendChild(label);
    });
    if (quickbuy && quickbuy.el === dialog) quickbuy.variantsById = byId;
    var priceEl = dialog.querySelector('[data-native-cart-quickbuy-price]');
    if (priceEl) priceEl.textContent = fromPriceText(product, live.variants);
    var img = dialog.querySelector('[data-native-cart-quickbuy-image]');
    if (img && product && product.image) img.src = product.image;
    if (quickbuy) quickbuy.selected = null;
    syncQuickbuyAdd(dialog);
  }

  function showQuickbuyFallback(dialog, product) {
    if (!dialog) return;
    setQuickbuyNote(dialog, '');
    setQuickbuyError(dialog, strings().addError || 'This item could not be added.');
    var choices = dialog.querySelector('[data-native-cart-quickbuy-choices]');
    if (!choices) return;
    choices.replaceChildren();
    var retry = dialog.ownerDocument.createElement('button');
    retry.type = 'button';
    retry.className = 'native-cart-quickbuy__retry';
    retry.setAttribute('data-native-cart-quickbuy-retry', '');
    retry.textContent = strings().retry || 'Try again';
    var link = dialog.ownerDocument.createElement('a');
    link.className = 'native-cart-quickbuy__details';
    link.setAttribute('data-native-cart-rec-choose', '');
    link.href = productUrl(product);
    link.textContent = strings().chooseOptions || 'Choose options';
    choices.appendChild(retry);
    choices.appendChild(link);
    if (quickbuy && quickbuy.el === dialog) quickbuy.selected = null;
    syncQuickbuyAdd(dialog);
  }

  function loadQuickbuyProduct(session, dialog, product, handle) {
    setQuickbuyNote(dialog, strings().loadingOptions || 'Loading options…');
    setQuickbuyError(dialog, '');
    var choices = dialog.querySelector('[data-native-cart-quickbuy-choices]');
    if (choices) choices.replaceChildren();
    syncQuickbuyAdd(dialog);
    loadVariants(handle).then(function (live) {
      if (session !== quickbuySession || !quickbuy || quickbuy.el !== dialog || !dialog.isConnected) return;
      var expected = String((product && product.id) || '');
      if (!live || String(live.id) !== expected) {
        showQuickbuyFallback(dialog, product);
        return;
      }
      renderQuickbuyChoices(dialog, live, product);
    }).catch(function () {
      if (session !== quickbuySession || !quickbuy || quickbuy.el !== dialog || !dialog.isConnected) return;
      showQuickbuyFallback(dialog, product);
    });
  }

  function openQuickbuy(row, trigger) {
    if (!row || typeof document === 'undefined') return;
    destroyQuickbuy(false);
    var session = ++quickbuySession;
    var product = row.__product || {};
    var doc = row.ownerDocument || document;
    var dialog = doc.createElement('dialog');
    dialog.className = 'native-cart-quickbuy';
    dialog.setAttribute('data-native-cart-quickbuy', '');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'NativeCartQuickbuyTitle');

    var sheet = doc.createElement('div');
    sheet.className = 'native-cart-quickbuy__sheet';
    sheet.setAttribute('data-native-cart-quickbuy-sheet', '');

    var bar = doc.createElement('div');
    bar.className = 'native-cart-quickbuy__bar';
    var back = doc.createElement('button');
    back.type = 'button';
    back.className = 'native-cart-quickbuy__back';
    back.setAttribute('data-native-cart-quickbuy-back', '');
    back.textContent = strings().back || 'Back';
    var close = doc.createElement('button');
    close.type = 'button';
    close.className = 'native-cart-quickbuy__close';
    close.setAttribute('data-native-cart-quickbuy-close', '');
    close.setAttribute('aria-label', strings().close || 'Close');
    close.textContent = '×';
    bar.appendChild(back);
    bar.appendChild(close);

    var summary = doc.createElement('div');
    summary.className = 'native-cart-quickbuy__summary';
    var media = doc.createElement('div');
    media.className = 'native-cart-quickbuy__media';
    if (product.image) {
      var img = doc.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.width = 64;
      img.height = 64;
      img.setAttribute('data-native-cart-quickbuy-image', '');
      media.appendChild(img);
    } else {
      var imgLater = doc.createElement('img');
      imgLater.alt = '';
      imgLater.hidden = true;
      imgLater.width = 64;
      imgLater.height = 64;
      imgLater.setAttribute('data-native-cart-quickbuy-image', '');
      media.appendChild(imgLater);
    }
    var copy = doc.createElement('div');
    copy.className = 'native-cart-quickbuy__copy';
    var title = doc.createElement('h2');
    title.className = 'native-cart-quickbuy__title';
    title.id = 'NativeCartQuickbuyTitle';
    title.textContent = product.title || '';
    var price = doc.createElement('p');
    price.className = 'native-cart-quickbuy__price';
    price.setAttribute('data-native-cart-quickbuy-price', '');
    price.textContent = fromPriceText(product, null);
    copy.appendChild(title);
    copy.appendChild(price);
    summary.appendChild(media);
    summary.appendChild(copy);

    var note = doc.createElement('p');
    note.className = 'native-cart-quickbuy__note';
    note.setAttribute('data-native-cart-quickbuy-note', '');
    note.setAttribute('role', 'status');
    note.hidden = true;

    var legend = doc.createElement('p');
    legend.className = 'native-cart-quickbuy__legend';
    legend.id = 'NativeCartQuickbuyChoices';
    legend.textContent = strings().chooseOption || 'Choose option';
    var choices = doc.createElement('div');
    choices.className = 'native-cart-quickbuy__choices';
    choices.setAttribute('data-native-cart-quickbuy-choices', '');
    choices.setAttribute('role', 'radiogroup');
    choices.setAttribute('aria-labelledby', 'NativeCartQuickbuyChoices');

    var status = doc.createElement('p');
    status.className = 'native-cart-quickbuy__status';
    status.setAttribute('data-native-cart-quickbuy-status', '');
    status.setAttribute('role', 'alert');
    status.hidden = true;

    var add = doc.createElement('button');
    add.type = 'button';
    add.className = 'native-cart-quickbuy__add';
    add.setAttribute('data-native-cart-rec-add', '');
    add.disabled = true;
    var spinner = doc.createElement('span');
    spinner.className = 'native-cart-recs__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    var addLabel = doc.createElement('span');
    addLabel.setAttribute('data-native-cart-rec-add-label', '');
    addLabel.textContent = strings().add || 'Add';
    add.appendChild(spinner);
    add.appendChild(addLabel);

    sheet.appendChild(bar);
    sheet.appendChild(summary);
    sheet.appendChild(note);
    sheet.appendChild(legend);
    sheet.appendChild(choices);
    sheet.appendChild(status);
    sheet.appendChild(add);
    dialog.appendChild(sheet);

    var placement = mountPlacement(row.closest && row.closest(MOUNT));
    var host = quickbuyHost(placement);
    if (!host) return;
    if (host.tagName === 'FORM') host = doc.body || host;
    host.appendChild(dialog);
    quickbuy = {
      el: dialog,
      placement: placement,
      session: session,
      trigger: trigger || null,
      row: row,
      product: product,
      selected: null,
      variantsById: {}
    };
    if (typeof dialog.showModal === 'function') {
      try { dialog.showModal(); } catch (err) { dialog.setAttribute('open', ''); }
    } else {
      dialog.setAttribute('open', '');
    }
    dialog.addEventListener('cancel', function (evt) {
      evt.preventDefault();
      swallowEscape = true;
      destroyQuickbuy(true);
    });
    dialog.addEventListener('click', function (evt) {
      if (evt.target === dialog) destroyQuickbuy(true);
    });
    back.focus();
    var handle = row.getAttribute('data-handle') || product.handle;
    loadQuickbuyProduct(session, dialog, product, handle);
  }

  function renderRow(doc, product, position, engine) {
    var row = doc.createElement('article');
    row.className = 'native-cart-recs__row';
    row.setAttribute('data-native-cart-rec-row', '');
    row.setAttribute('data-product-id', String(product.id || ''));
    row.setAttribute('data-handle', String(product.handle || ''));
    row.setAttribute('data-variant-count', String(product.variantCount || 1));
    row.setAttribute('data-purchase-mode', product.purchaseMode || (Number(product.variantCount) === 1 ? 'direct' : 'options'));
    row.setAttribute('data-position', String(position));
    if (isDirect(product) && product.variantId) {
      row.setAttribute('data-variant-id', String(product.variantId));
    }

    var media = doc.createElement('figure');
    media.className = 'native-cart-recs__media';
    if (product.image) {
      var img = doc.createElement('img');
      img.src = product.image;
      img.alt = '';
      img.width = 56;
      img.height = 56;
      media.appendChild(img);
    }
    row.appendChild(media);

    var body = doc.createElement('div');
    body.className = 'native-cart-recs__body';
    body.setAttribute('data-native-cart-rec-body', '');
    var title = doc.createElement('p');
    title.className = 'native-cart-recs__title';
    title.textContent = product.title || '';
    body.appendChild(title);
    var price = doc.createElement('p');
    price.className = 'native-cart-recs__price';
    price.setAttribute('data-native-cart-rec-price', '');
    price.textContent = formatMoney(product.price != null ? product.price : product.priceCents);
    body.appendChild(price);
    row.appendChild(body);

    if (isDirect(product)) {
      var add = doc.createElement('button');
      add.type = 'button';
      add.className = 'native-cart-recs__add';
      add.setAttribute('data-native-cart-rec-add', '');
      var addLabel = doc.createElement('span');
      addLabel.setAttribute('data-native-cart-rec-add-label', '');
      addLabel.textContent = strings().add || 'Add';
      var spinner = doc.createElement('span');
      spinner.className = 'native-cart-recs__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      add.appendChild(spinner);
      add.appendChild(addLabel);
      row.appendChild(add);
    } else {
      var choose = doc.createElement('button');
      choose.type = 'button';
      choose.className = 'native-cart-recs__add native-cart-recs__choose';
      choose.setAttribute('data-native-cart-rec-open', '');
      choose.textContent = strings().chooseOption || 'Choose option';
      row.appendChild(choose);
    }

    var status = doc.createElement('p');
    status.className = 'native-cart-recs__status';
    status.setAttribute('data-native-cart-rec-status', '');
    status.setAttribute('role', 'alert');
    status.setAttribute('aria-live', 'polite');
    status.hidden = true;
    row.appendChild(status);

    row.__product = product;
    if (engine && typeof engine.observe === 'function') {
      engine.observe(row, product, position);
    }
    applyRowNotice(row);
    return row;
  }

  function renderMount(el, products, engine) {
    var cfg = readConfig();
    var doc = el.ownerDocument;
    var card = doc.createElement('div');
    card.className = 'native-cart-recs';
    var heading = doc.createElement('h2');
    heading.className = 'native-cart-recs__heading';
    var mountTitle = el.getAttribute('data-title');
    heading.textContent = mountTitle || cfg.title;
    card.appendChild(heading);
    var list = doc.createElement('div');
    list.className = 'native-cart-recs__list';
    products.forEach(function (product, index) {
      list.appendChild(renderRow(doc, product, index, engine));
    });
    card.appendChild(list);
    el.replaceChildren(card);
  }

  function refresh(cartHint) {
    var token = ++rankToken;
    var engine = learning();
    var nodes = mounts();
    if (!engine || !nodes.length) {
      hideMounts(nodes);
      return Promise.resolve(null);
    }
    return currentCart(cartHint).then(function (cart) {
      if (token !== rankToken) return null;
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
        hideMounts(nodes);
        return null;
      }
      var identity = cartIdentity(cart);
      var hasRows = nodes.some(function (el) {
        return el.querySelector('[data-native-cart-rec-row]');
      });
      if (hasRows && identity && identity === lastRenderedIdentity) {
        return null;
      }
      hideMounts(nodes);
      return engine.rank(cart).then(function (ranked) {
        if (token !== rankToken) return null;
        var picks = Array.isArray(ranked) ? ranked.slice(0, readConfig().limit) : [];
        if (!picks.length) {
          hideMounts(nodes);
          return [];
        }
        lastRenderedIdentity = identity;
        nodes.forEach(function (el) {
          renderMount(el, picks.slice(0, mountLimit(el)), engine);
        });
        return picks;
      });
    }).catch(function () {
      if (token === rankToken) hideMounts();
      return null;
    });
  }

  var productToken = 0;
  var selectedSource = null;

  function refreshProduct(cartHint, variant) {
    var token = ++productToken;
    var engine = learning();
    var nodes = productMounts();
    if (!engine || typeof engine.rankProduct !== 'function' || !nodes.length) {
      nodes.forEach(function (el) { el.replaceChildren(); });
      return Promise.resolve(null);
    }
    var embedded = null;
    var dataNode = typeof document !== 'undefined' ? document.getElementById('NativeProductLearningData') : null;
    try { embedded = dataNode && dataNode.textContent ? JSON.parse(dataNode.textContent) : null; } catch (err) { embedded = null; }
    if (!embedded || embedded.enabled !== true || !embedded.source || !embedded.source.id) {
      nodes.forEach(function (el) { el.replaceChildren(); });
      return Promise.resolve(null);
    }
    if (variant && variant.id) {
      selectedSource = {
        productId: String(embedded.source.id),
        id: String(variant.id),
        title: variant.title || '',
        price: Number(variant.price)
      };
    } else if (selectedSource && selectedSource.productId !== String(embedded.source.id)) {
      selectedSource = null;
    }
    var cartReady;
    try { cartReady = currentCart(cartHint); } catch (err) { cartReady = Promise.resolve({ items: [] }); }
    return Promise.resolve(cartReady).catch(function () { return { items: [] }; }).then(function (cart) {
      if (token !== productToken) return null;
      var source = {
        id: String(embedded.source.id),
        variantId: embedded.source.variantId ? String(embedded.source.variantId) : '',
        variantTitle: embedded.source.variantTitle || '',
        priceCents: embedded.source.priceCents,
        limit: mountLimit(nodes[0])
      };
      if (selectedSource && selectedSource.productId === String(embedded.source.id)) {
        source.variantId = selectedSource.id;
        source.variantTitle = selectedSource.title || source.variantTitle;
        if (Number.isFinite(selectedSource.price)) source.priceCents = selectedSource.price;
      }
      return engine.rankProduct(source, cart || { items: [] }).then(function (ranked) {
        if (token !== productToken) return null;
        var picks = Array.isArray(ranked) ? ranked : [];
        nodes.forEach(function (el) {
          var slice = picks.slice(0, mountLimit(el));
          if (!slice.length) {
            el.replaceChildren();
            return;
          }
          renderMount(el, slice, engine);
        });
        return picks;
      });
    }).catch(function () {
      if (token === productToken) nodes.forEach(function (el) { el.replaceChildren(); });
      return null;
    });
  }

  function classifyFetchError(err) {
    if (!err) return err;
    if (err.code === 'invalid_json' || err.code === 'network' || err.name === 'TypeError') {
      err.uncertain = true;
      err.code = err.code || 'network';
    }
    return err;
  }

  function postAdd(variantId, properties) {
    var url = localizedUrl('cartAdd');
    var body = {
      id: Number(variantId) || variantId,
      quantity: 1
    };
    if (properties && Object.keys(properties).length) body.properties = properties;
    return root.fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.text().then(function (text) {
        return root.CaffeineCart.parseJsonResponse(response, text);
      });
    });
  }

  function performAdd(variantId, properties) {
    if (!INT_RE.test(String(Number(variantId) || variantId))) {
      var invalid = new Error('invalid_variant');
      invalid.code = 'invalid_variant';
      return Promise.reject(invalid);
    }
    var beforeQty = 0;
    return currentCart(null, true).then(function (cart) {
      beforeQty = root.CaffeineCart.quantityForVariant(cart, variantId);
      return postAdd(variantId, properties).then(
        function () {
          return { postError: null };
        },
        function (err) {
          return { postError: classifyFetchError(err) };
        }
      );
    }).then(function (result) {
      return currentCart(null, true).then(function (after) {
        var afterQty = root.CaffeineCart.quantityForVariant(after, variantId);
        var added = afterQty - beforeQty;
        if (added <= 0) {
          var fail = result.postError || new Error('not_confirmed');
          fail = classifyFetchError(fail);
          fail.code = fail.code || 'not_confirmed';
          fail.cart = after;
          fail.added = 0;
          fail.requested = 1;
          throw fail;
        }
        if (added < 1 || result.postError) {
          var partial = new Error('partial_add');
          partial.code = 'partial_add';
          partial.cart = after;
          partial.added = added;
          partial.requested = 1;
          throw partial;
        }
        return {
          cart: after,
          added: added,
          requested: 1,
          beforeQty: beforeQty,
          afterQty: afterQty
        };
      }).catch(function (err) {
        if (err && err.cart) throw err;
        err = classifyFetchError(err);
        err.uncertain = true;
        throw err;
      });
    });
  }

  function settleCart(token, cart) {
    if (cart && root.CaffeineCart.isFullCart(cart)) {
      root.CaffeineCart.setLastCart(cart);
    }
    if (!root.CaffeineCart.refreshViews || !root.theme || !root.theme.cart) {
      return Promise.resolve(cart);
    }
    return root.CaffeineCart.refreshViews(function () {
      return root.theme.cart.getCartProductMarkup().then(function (payload) {
        return payload && payload.text;
      });
    }, token).then(function () {
      return cart;
    });
  }

  function errorMessage(err) {
    if (err && err.uncertain) return strings().addUncertain || 'We could not confirm this item was added. Check your cart before trying again.';
    if (err && err.code === 'partial_add') return strings().partialAdd || 'Only some of this item could be added. Check the quantity in your cart.';
    return (err && err.payload && (err.payload.description || err.payload.message)) || strings().addError || 'This item could not be added.';
  }

  function liveRow(previous) {
    if (!previous || !previous.getAttribute || typeof document === 'undefined') return previous;
    var id = previous.getAttribute('data-product-id');
    var handle = previous.getAttribute('data-handle');
    var found = null;
    mounts().forEach(function (mount) {
      mount.querySelectorAll('[data-native-cart-rec-row]').forEach(function (row) {
        if (row.getAttribute('data-product-id') === id && row.getAttribute('data-handle') === handle) {
          found = row;
        }
      });
    });
    return found || previous;
  }

  function onQuickbuyChange(evt) {
    var input = evt.target && evt.target.closest && evt.target.closest('[data-native-cart-quickbuy-variant]');
    if (!input || !quickbuy || !quickbuy.el || !quickbuy.el.contains(input)) return;
    if (input.disabled) return;
    var variant = quickbuy.variantsById && quickbuy.variantsById[input.value];
    if (!variant || variant.available !== true) {
      input.checked = false;
      return;
    }
    applySelectedVariant(variant);
  }

  function onOpenClick(evt) {
    var button = evt.target && evt.target.closest && evt.target.closest('[data-native-cart-rec-open]');
    if (!button) return;
    evt.preventDefault();
    var row = button.closest('[data-native-cart-rec-row]');
    if (!row) return;
    openQuickbuy(row, button);
  }

  function onQuickbuyControls(evt) {
    if (!quickbuy || !quickbuy.el || !evt.target || !evt.target.closest) return;
    if (!quickbuy.el.contains(evt.target)) return;
    if (evt.target.closest('[data-native-cart-quickbuy-back], [data-native-cart-quickbuy-close]')) {
      evt.preventDefault();
      destroyQuickbuy(true);
      return;
    }
    var retry = evt.target.closest('[data-native-cart-quickbuy-retry]');
    if (!retry) return;
    evt.preventDefault();
    var session = quickbuy.session;
    var dialog = quickbuy.el;
    var product = quickbuy.product;
    var handle = (quickbuy.row && quickbuy.row.getAttribute('data-handle')) || (product && product.handle);
    loadQuickbuyProduct(session, dialog, product, handle);
  }

  function commitAdd(row, variantId, button, product, fromQuickbuy) {
    if (!row || pendingRows.has(row) || row.classList.contains('is-loading') || row.classList.contains('is-confirmed')) return;
    var engine = learning();
    if (pendingVariants.has(variantId)) return;
    if (!variantId || !INT_RE.test(String(variantId)) || (button && button.disabled)) {
      if (fromQuickbuy && quickbuy && quickbuy.el) setQuickbuyError(quickbuy.el, strings().chooseOption || 'Choose option');
      else setStatus(row, strings().chooseOption || 'Choose option', true);
      return;
    }
    var addPlacement = mountPlacement(row.closest && row.closest(MOUNT));
    var snapshot = cloneProduct(product);
    var addDialog = fromQuickbuy && quickbuy ? quickbuy.el : null;
    var addSession = fromQuickbuy && quickbuy ? quickbuy.session : 0;
    var attrs = engine && typeof engine.attributes === 'function' && snapshot ? engine.attributes(snapshot) : {};
    if (engine && typeof engine.event === 'function' && snapshot) engine.event('click', snapshot);
    pendingVariants.add(variantId);
    setRowPending(row, true);
    if (fromQuickbuy && addDialog) {
      addDialog.classList.add('is-loading');
      lockQuickbuyChoices(addDialog, true);
      syncQuickbuyAdd(addDialog);
    }
    root.CaffeineCart.queueMutation(function (token) {
      return performAdd(variantId, attrs).then(
        function (result) {
          return settleCart(token, result.cart).then(function () {
            return result;
          }).catch(function (err) { err.confirmed = true; err.cart = result.cart; throw err; });
        },
        function (err) {
          if (err && err.cart) {
            return settleCart(token, err.cart).then(function () {
              throw err;
            });
          }
          return settleCart(token).then(function () {
            throw err;
          }, function () {
            throw err;
          });
        }
      );
    }).then(function () {
      pendingVariants.delete(variantId);
      rowNotice = null;
      if (addPlacement === 'product_page') {
        if (sameQuickbuy(addDialog, addSession)) destroyQuickbuy(false);
      } else if (sameQuickbuy(addDialog, addSession)) destroyQuickbuy(true);
      var live = liveRow(row);
      if (live && live.isConnected) setRowPending(live, false);
      if (engine && typeof engine.event === 'function' && snapshot) engine.event('add_committed', snapshot);
      if (addPlacement === 'product_page') {
        document.dispatchEvent(new CustomEvent('cart:open'));
        var drawer = document.getElementById('CartDrawer');
        var focusTarget = drawer && (drawer.querySelector('.js-drawer-close') || drawer);
        if (focusTarget && focusTarget.focus) {
          if (focusTarget === drawer && !drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
          focusTarget.focus();
        }
      }
    }).catch(function (err) {
      pendingVariants.delete(variantId);
      var message = err && err.confirmed ? 'Added to your cart. Refresh the cart to see the update.' : errorMessage(err);
      rememberRowNotice(row, message, !!(err && err.confirmed));
      var live = liveRow(row);
      if (live && live.isConnected) {
        setRowPending(live, false);
        setStatus(live, message, true);
        if (err && err.confirmed) {
          var add = live.querySelector('[data-native-cart-rec-add]');
          if (add) add.disabled = true;
          live.classList.add('is-confirmed');
        }
      }
      if (sameQuickbuy(addDialog, addSession) && !(err && err.confirmed)) {
        addDialog.classList.remove('is-loading');
        lockQuickbuyChoices(addDialog, false);
        setQuickbuyError(addDialog, message);
        syncQuickbuyAdd(addDialog);
      } else if (sameQuickbuy(addDialog, addSession)) {
        destroyQuickbuy(true);
      }
      if (engine && typeof engine.event === 'function' && snapshot) engine.event(err && err.confirmed ? 'add_committed' : 'add_error', snapshot);
    });
  }

  function onAddClick(evt) {
    var button = evt.target && evt.target.closest && evt.target.closest('[data-native-cart-rec-add]');
    if (!button) return;
    evt.preventDefault();
    if (quickbuy && quickbuy.el && quickbuy.el.contains(button)) {
      var variant = quickbuy.selected;
      var chosen = quickbuy.product;
      if (!variant || variant.available !== true) {
        setQuickbuyError(quickbuy.el, strings().chooseOption || 'Choose option');
        return;
      }
      var snapshot = cloneProduct(chosen);
      if (snapshot) {
        snapshot.variantId = String(variant.id);
        snapshot.variantTitle = variant.title || '';
        snapshot.price = Number(variant.price);
      }
      commitAdd(quickbuy.row, String(variant.id), button, snapshot, true);
      return;
    }
    var row = button.closest('[data-native-cart-rec-row]');
    if (!row) return;
    var variantId = selectedVariantId(row);
    if (!variantId || !addEnabled(row) || button.disabled) {
      setStatus(row, strings().chooseOption || 'Choose option', true);
      return;
    }
    commitAdd(row, variantId, button, row.__product, false);
  }

  function onCartChanged(evt) {
    variantCache = {};
    var cart = evt && evt.detail && evt.detail.cart;
    refresh(cart);
    refreshProduct(cart);
  }

  function bind() {
    if (typeof document === 'undefined' || bind.bound) return;
    bind.bound = true;
    document.addEventListener('click', function (evt) {
      onOpenClick(evt);
      onQuickbuyControls(evt);
      onAddClick(evt);
      var closer = evt.target && evt.target.closest && evt.target.closest('.js-drawer-close');
      if (closer) destroyQuickbuy(false);
    });
    document.addEventListener('change', onQuickbuyChange);
    document.addEventListener('cart:close', function () { destroyQuickbuy(false); });
    document.addEventListener('caffeine:cart:changed', onCartChanged);
    document.addEventListener('caffeine:cart:open', onCartChanged);
    document.addEventListener('caffeine:learning:ready', function () { refreshProduct(); });
    document.addEventListener('variant:change', function (evt) {
      var detail = evt && evt.detail;
      var variant = detail && (detail.variant || detail);
      refreshProduct(null, variant);
    });
    window.addEventListener('keydown', onQuickbuyKeyDown, true);
    window.addEventListener('keyup', onQuickbuyKeyUp, true);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialRefresh);
    } else {
      initialRefresh();
    }
  }

  // The drawer renders when it opens, but the /cart page never opens it, so
  // its slot is filled on load from a fresh /cart.js.
  function hasCartPageMount() {
    return mounts().some(function (el) {
      return !!(el.closest && el.closest('[data-native-cart-surface="cart_page"]'));
    });
  }

  function initialRefresh() {
    if (root.CaffeineCart && typeof root.CaffeineCart.getLastCart === 'function' && root.CaffeineCart.isFullCart(root.CaffeineCart.getLastCart())) {
      refresh(root.CaffeineCart.getLastCart());
    } else if (hasCartPageMount()) {
      refresh();
    }
    refreshProduct();
  }

  var api = {
    refresh: refresh,
    refreshProduct: refreshProduct,
    performAdd: performAdd,
    readConfig: readConfig,
    bind: bind,
    mounts: mounts,
    hideMounts: hideMounts,
    isDirect: isDirect,
    formatMoney: formatMoney,
    loadVariants: loadVariants
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  root.NativeCartRecommendations = api;
  if (typeof document !== 'undefined') bind();
})(typeof window !== 'undefined' ? window : globalThis);
